// src/components/pipelines/sheets/ActionBuilder.jsx
import React, { useState } from 'react';
import { Button } from '../../ui/button.tsx';
import { Input } from '../../ui/input.tsx';
import { Label } from '../../ui/label.tsx';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../ui/select.tsx';
import { Card } from '../../ui/card.tsx';
import { Textarea } from '../../ui/textarea.tsx';

/**
 * ActionBuilder - Form for creating action configurations
 *
 * Returns action object structure for AutomationRule.actions array
 */
const ActionBuilder = ({ onSave, onCancel, initialData = null }) => {
  const [actionType, setActionType] = useState(initialData?.type || '');
  const [actionName, setActionName] = useState(initialData?.name || '');
  const [templateId, setTemplateId] = useState(initialData?.template_id || '');
  const [subject, setSubject] = useState(initialData?.subject || '');
  const [message, setMessage] = useState(initialData?.message || '');
  const [taskDescription, setTaskDescription] = useState(initialData?.description || '');
  const [assigneeId, setAssigneeId] = useState(initialData?.assignee_id || '');

  const handleSave = () => {
    if (!actionType) {
      alert('Please select an action type');
      return;
    }

    const actionData = {
      type: actionType,
      name: actionName || `${actionType.replace(/_/g, ' ')} action`
    };

    // Add type-specific fields
    switch (actionType) {
      case 'send_email':
        if (!templateId) {
          alert('Please enter a template ID');
          return;
        }
        actionData.template_id = templateId;
        actionData.subject = subject;
        actionData.to = '{{customer.email}}'; // Default template variable
        break;

      case 'send_sms':
        if (!templateId) {
          alert('Please enter a template ID');
          return;
        }
        actionData.template_id = templateId;
        actionData.message = message;
        actionData.to = '{{customer.phone}}'; // Default template variable
        break;

      case 'create_task':
        if (!taskDescription) {
          alert('Please enter a task description');
          return;
        }
        actionData.description = taskDescription;
        if (assigneeId) {
          actionData.assignee_id = assigneeId;
        }
        break;

      case 'wait':
        // Wait action doesn't need additional fields for now
        // Could add duration here if needed
        break;

      case 'webhook':
        // Placeholder for future webhook support
        break;

      default:
        break;
    }

    onSave(actionData);
  };

  return (
    <Card className="p-4 bg-slate-800/70 border-slate-700">
      <div className="space-y-4">
        {/* Action Name */}
        <div>
          <Label htmlFor="action-name" className="text-sm text-slate-300">
            Action Name (optional)
          </Label>
          <Input
            id="action-name"
            value={actionName}
            onChange={(e) => setActionName(e.target.value)}
            placeholder="e.g., Send welcome email"
            className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
          />
        </div>

        {/* Action Type */}
        <div>
          <Label htmlFor="action-type" className="text-sm text-slate-300">
            Action Type <span className="text-red-400">*</span>
          </Label>
          <Select value={actionType} onValueChange={setActionType}>
            <SelectTrigger className="mt-1 bg-slate-900 border-slate-700 text-slate-200">
              <SelectValue placeholder="Select action type" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              <SelectItem value="send_email" className="text-slate-200">Send Email</SelectItem>
              <SelectItem value="send_sms" className="text-slate-200">Send SMS</SelectItem>
              <SelectItem value="create_task" className="text-slate-200">Create Task</SelectItem>
              <SelectItem value="wait" className="text-slate-200">Wait/Delay</SelectItem>
              <SelectItem value="webhook" className="text-slate-200" disabled>
                Webhook (Coming Soon)
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Type-Specific Fields */}
        {actionType === 'send_email' && (
          <>
            <div>
              <Label htmlFor="email-template" className="text-sm text-slate-300">
                Email Template ID <span className="text-red-400">*</span>
              </Label>
              <Input
                id="email-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                placeholder="Enter template ID"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
              />
              <p className="text-xs text-slate-500 mt-1">
                You can find template IDs in Settings → Email Templates
              </p>
            </div>
            <div>
              <Label htmlFor="email-subject" className="text-sm text-slate-300">
                Subject (optional override)
              </Label>
              <Input
                id="email-subject"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g., Welcome to our service!"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
              />
            </div>
          </>
        )}

        {actionType === 'send_sms' && (
          <>
            <div>
              <Label htmlFor="sms-template" className="text-sm text-slate-300">
                SMS Template ID <span className="text-red-400">*</span>
              </Label>
              <Input
                id="sms-template"
                value={templateId}
                onChange={(e) => setTemplateId(e.target.value)}
                placeholder="Enter template ID"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
              />
              <p className="text-xs text-slate-500 mt-1">
                You can find template IDs in Settings → SMS Templates
              </p>
            </div>
            <div>
              <Label htmlFor="sms-message" className="text-sm text-slate-300">
                Message (optional override)
              </Label>
              <Textarea
                id="sms-message"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="e.g., Thanks for your purchase!"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
                rows={3}
              />
            </div>
          </>
        )}

        {actionType === 'create_task' && (
          <>
            <div>
              <Label htmlFor="task-description" className="text-sm text-slate-300">
                Task Description <span className="text-red-400">*</span>
              </Label>
              <Textarea
                id="task-description"
                value={taskDescription}
                onChange={(e) => setTaskDescription(e.target.value)}
                placeholder="e.g., Follow up with customer"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
                rows={3}
              />
            </div>
            <div>
              <Label htmlFor="task-assignee" className="text-sm text-slate-300">
                Assign To (User ID - optional)
              </Label>
              <Input
                id="task-assignee"
                value={assigneeId}
                onChange={(e) => setAssigneeId(e.target.value)}
                placeholder="Enter user ID"
                className="mt-1 bg-slate-900 border-slate-700 text-slate-200"
              />
            </div>
          </>
        )}

        {actionType === 'wait' && (
          <div className="p-3 bg-slate-900/50 rounded-md border border-slate-700">
            <p className="text-sm text-slate-400">
              Wait/delay action will be configured in a future update. For now, this will create a placeholder action.
            </p>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2 pt-2">
          <Button
            variant="default"
            size="sm"
            onClick={handleSave}
            className="flex-1 bg-green-600 hover:bg-green-700"
          >
            Save Action
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={onCancel}
            className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
          >
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
};

export default ActionBuilder;
